import React, { useCallback, useMemo } from 'react';
import { Button } from '@nextui-org/react';
import { Settlement } from 'common/src/utils/types';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import VarietyFilter from '../../../../../common/src/components/filters/VarietyFilter';
import FormatFilter from '../../../../../common/src/components/filters/FormatFilter';
import CaliberFilter from '../../../../../common/src/components/filters/CaliberFilter';
import ColorFilter from '../../../../../common/src/components/filters/ColorFilter';
import MarketFilter from '../../../../../common/src/components/filters/MarketFilter';
import ReceiverFilter from '../../../../../common/src/components/filters/ReceiverFilter';
import LabelFilter from '../../../../../common/src/components/filters/LabelFilter';
import FleetFilter from '../../../../../common/src/components/filters/FleetFilter';
import CountryChartFilter from '../../../../../common/src/components/filters/CountryChartFilter';
import BusinessTypeFilter from '../../../../../common/src/components/filters/BusinessTypeFilter';
import useDataStore from '../../../../../common/src/stores/useDataStore';

interface FiltersSectionProps {
  settlements: Settlement[];
  primaryFilter:
  | 'variety'
  | 'format'
  | 'caliber'
  | 'color'
  | 'market'
  | 'label'
  | 'receiver'
  | 'fleet'
  | 'country'
  | 'business_type';
  filters: {
    variety: string[];
    caliber: string[];
    color: string;
    format: string[];
    market: string[];
    label: string[];
    receiver: string[];
    fleet: string[];
    country: string[];
    business_type: string[];
  };
  onFilterChange: (filterName: string, value: string | string[]) => void;
  onClearFilters?: () => void;
}

const ChartsFiltersSection = ({
  settlements,
  primaryFilter,
  filters,
  onFilterChange,
  onClearFilters,
}: FiltersSectionProps) => {
  const { t } = useTranslation('common');
  // Datos procesados para evitar recálculos
  const { selectedSpecies } = useDataStore();
  const settlementDetails = useMemo(() => {
    return settlements.flatMap((s) => s.settlement_details || []);
  }, [settlements]);

  // Configuración de los filtros
  const filterComponents = useMemo(() => {
    const components: Record<string, React.ReactNode> = {
      variety: (
        <VarietyFilter
          data={settlementDetails}
          selectedVariety={filters.variety}
          onSelectionChange={(value) => onFilterChange('variety', value)}
          allowAllVarieties={true}
          selectionMode='multiple'
          defaultSelectAll
        />
      ),
      format: (
        <FormatFilter
          data={settlementDetails}
          selectedFormat={filters.format}
          onSelectionChange={(value) =>
            onFilterChange('format', Array.from(value))
          }
          selectionMode='multiple'
          defaultSelectAll
        />
      ),
      caliber: (
        <CaliberFilter
          data={settlementDetails}
          selectedCaliber={filters.caliber}
          onSelectionChange={(value) => onFilterChange('caliber', value)}
          selectionMode='multiple'
          defaultSelectAll
        />
      ),
      market: (
        <MarketFilter
          data={settlements}
          selectedMarket={filters.market}
          onSelectionChange={(value) => {
            onFilterChange('market', Array.from(value));
          }}
          selectionMode='multiple'
          defaultSelectAll
        />
      ),
      label: (
        <LabelFilter
          data={settlementDetails}
          selectedLabel={filters.label}
          onSelectionChange={(value) => onFilterChange('label', value)}
          selectionMode='multiple'
          defaultSelectAll
        />
      ),
      receiver: (
        <ReceiverFilter
          data={settlements}
          selectedReceiver={filters.receiver}
          onSelectionChange={(value) => onFilterChange('receiver', value)}
          selectionMode='multiple'
          defaultSelectAll
        />
      ),
      fleet: (
        <FleetFilter
          data={settlements}
          selectedFleet={filters.fleet}
          onSelectionChange={(value) => onFilterChange('fleet', value)}
          selectionMode='multiple'
          defaultSelectAll
        />
      ),
      country: (
        <CountryChartFilter
          data={settlements}
          selectedCountry={filters.country}
          onSelectionChange={(value) => onFilterChange('country', value)}
          selectionMode='multiple'
          defaultSelectAll
        />
      ),
      business_type: (
        <BusinessTypeFilter
          data={settlements}
          selectedBusinessType={filters.business_type}
          onSelectionChange={(value) => onFilterChange('business_type', value)}
          selectionMode='multiple'
          defaultSelectAll
        />
      ),
    };

    // Only add color filter if the selected species has color support
    if (selectedSpecies?.has_color) {
      components.color = (
        <ColorFilter
          data={settlements}
          selectedColor={filters.color}
          onSelectionChange={(value) =>
            onFilterChange('color', Array.from(value))
          }
          selectionMode='multiple'
          defaultSelectAll
        />
      );
    }

    return components;
  }, [
    settlements,
    settlementDetails,
    filters,
    onFilterChange,
    selectedSpecies,
  ]);

  const filterOrder = ['receiver', 'variety', 'caliber', 'format', 'color', 'country', 'market', 'label', 'fleet', 'business_type'];

  // Compute available option counts to detect if any filter is narrowed
  const availableCounts = useMemo(() => {
    const uniqueReceivers = new Set(settlements.map((s) => s.receiver)).size;
    const uniqueVarieties = new Set(settlementDetails.map((d) => d.variety?.toUpperCase()).filter(Boolean)).size;
    const uniqueCalibers = new Set(settlementDetails.map((d) => d.caliber).filter(Boolean)).size;
    const uniqueFormats = new Set(settlementDetails.map((d) => d.format).filter(Boolean)).size;
    const uniqueMarkets = new Set(settlements.map((s) => s.market?.name || 'Sin Mercado')).size;
    const uniqueLabels = new Set(settlementDetails.map((d) => d.label || 'Sin Etiqueta')).size;
    const uniqueFleets = new Set(settlements.map((s) => {
      const t = s.ship_trip?.ship?.type;
      return t === 'A' ? 'AIR' : t === 'T' ? 'GROUND' : 'OCEAN';
    })).size;
    const uniqueCountries = new Set(settlements.map((s) => s.market?.country || 'Sin País')).size;
    const uniqueBusinessTypes = new Set(settlements.map((s) => s.business_type || 'Sin Tipo de Negocio')).size;
    const uniqueColors = new Set(
      settlementDetails.map((d) => d.color || 'Sin Color')
    ).size;

    return {
      receiver: uniqueReceivers,
      variety: uniqueVarieties,
      caliber: uniqueCalibers,
      format: uniqueFormats,
      color: uniqueColors,
      market: uniqueMarkets,
      label: uniqueLabels,
      fleet: uniqueFleets,
      country: uniqueCountries,
      business_type: uniqueBusinessTypes,
    };
  }, [settlements, settlementDetails]);

  const hasActiveFilter = useMemo(() => {
    const check = (key: string) => {
      const val = (filters as any)[key];
      if (!val || !Array.isArray(val)) return false;
      if (val.length === 0) return false;
      const total = (availableCounts as any)[key] || 0;
      return val.length < total;
    };
    return filterOrder.some(check);
  }, [filters, availableCounts, filterOrder]);

  return (
    <div className='flex gap-4 flex-col w-full'>
      <div className='grid grid-cols-1 md:grid-cols-5 gap-4'>
        {filterOrder.map((key) => filterComponents[key] ? <React.Fragment key={key}>{filterComponents[key]}</React.Fragment> : null)}
      </div>
      {hasActiveFilter && onClearFilters && (
        <div className='flex justify-end'>
          <Button
            size='sm'
            variant='light'
            color='danger'
            onPress={onClearFilters}
          >
            {t('Limpiar filtros')}
          </Button>
        </div>
      )}
    </div>
  );
};

// Añadir React.memo con comparación personalizada
export default React.memo(ChartsFiltersSection, (prevProps, nextProps) => {
  // Re-renderizar si cambia el filtro primario
  if (prevProps.primaryFilter !== nextProps.primaryFilter) {
    return false;
  }

  // Verificar cambios en los settlements usando IDs
  const prevIds = prevProps.settlements.map((s) => s.id).join(',');
  const nextIds = nextProps.settlements.map((s) => s.id).join(',');
  if (prevIds !== nextIds) {
    return false;
  }

  // Comparación simple de la función onFilterChange
  if (prevProps.onFilterChange !== nextProps.onFilterChange) {
    return false;
  }

  if (prevProps.onClearFilters !== nextProps.onClearFilters) {
    return false;
  }

  // Compare filters to detect changes for clear button visibility
  if (JSON.stringify(prevProps.filters) !== JSON.stringify(nextProps.filters)) {
    return false;
  }

  // Evitar re-render para cambios en filtros ya que son manejados por los componentes individuales
  return true;
});
